/**
 * MasterFormulaHeader — collapsible header for the master formula panel.
 *
 * Single source of truth for the group score expression in blocks mode:
 * expanded preview text, validation badge, and optional subformula hint.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useMemo } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

import { expandSubrefsForDisplay } from '@trafficpulse/shared';
import type { FormulaSubformula } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

import {
  evaluateExpressionState,
  expressionStatusLabel,
} from '../group/expression-preview.state';

export interface MasterFormulaHeaderProps {
  open: boolean;
  onToggle: () => void;
  masterExpression: string;
  subformulas: FormulaSubformula[];
}

const MasterFormulaHeader: FC<MasterFormulaHeaderProps> = ({
  open,
  onToggle,
  masterExpression,
  subformulas,
}) => {
  const canonical = (masterExpression ?? '').trim();
  const expanded = useMemo(
    () => (canonical ? expandSubrefsForDisplay(canonical, subformulas).trim() : ''),
    [canonical, subformulas],
  );
  const state = useMemo(() => evaluateExpressionState(canonical), [canonical]);
  const previewText =
    expanded ||
    (subformulas.length === 0
      ? 'Add a subformula to start composing.'
      : 'Empty — combine subformulas (and metrics, if needed).');
  const showExpandedHint =
    Boolean(subformulas.length) && Boolean(canonical) && expanded !== canonical;

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <button
          type="button"
          aria-expanded={open}
          onClick={onToggle}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:text-foreground">
            {open ? (
              <ChevronDown className="h-3.5 w-3.5" aria-hidden />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" aria-hidden />
            )}
          </span>
          <h4 className="shrink-0 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Master formula
          </h4>
          <code className="min-w-0 flex-1 truncate rounded border border-border bg-background px-2 py-0.5 font-mono text-[11px] text-foreground">
            {previewText}
          </code>
        </button>
        <span
          className={cn(
            'shrink-0 text-xs',
            state.isValid ? 'text-success' : 'text-destructive',
          )}
        >
          {expressionStatusLabel(state)}
        </span>
      </div>
      {showExpandedHint ? (
        <p className="pl-7 text-[10px] text-muted-foreground">
          Full calculation (subformulas expanded). Saved config still uses internal
          references for scoring.
        </p>
      ) : null}
      {state.error ? (
        <p className="pl-7 text-[11px] text-destructive">{state.error}</p>
      ) : null}
    </div>
  );
};

export default MasterFormulaHeader;
